import {
  availabilityReasonField,
  endField,
  isOpenEndedField,
  notesField,
  repeatEndInclusiveField,
  startField,
  startImmediatelyField,
} from 'pages/Availability/common/FormDrawer/sharedValidationFields';
import { AvailabilityFormDrawerEnabledFields } from 'pages/Availability/common/FormDrawer/types';
import { object, string } from 'yup';

export const newAvailabilityEntryValidationSchema = (
  maxExportPower: number,
  maxImportPower: number,
  maxEnergyCapacity: number,
  enabledFields: AvailabilityFormDrawerEnabledFields,
) =>
  object()
    .shape({
      start: startField(enabledFields),
      end: endField(),
      startImmediately: startImmediatelyField(),
      isOpenEnded: isOpenEndedField(),
      availabilityReason: availabilityReasonField(),
      notes: notesField(),
      maxImportPower: string()
        .nullable()
        .transform((value) =>
          value === null || value === undefined ? '' : value,
        )
        .test(
          'is-valid-number',
          'Import limit must be a valid number.',
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return !isNaN(Number(value));
          },
        )
        .test(
          'min-value',
          'Import limit must be greater than or equal to zero.',
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return Number(value) >= 0;
          },
        )
        .test(
          'max-value',
          `Import limit must be less than or equal to ${maxImportPower} MW.`,
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return Number(value) <= maxImportPower;
          },
        ),
      maxExportPower: string()
        .nullable()
        .transform((value) =>
          value === null || value === undefined ? '' : value,
        )
        .test(
          'is-valid-number',
          'Export limit must be a valid number.',
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return !isNaN(Number(value));
          },
        )
        .test(
          'min-value',
          'Export limit must be greater than or equal to zero.',
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return Number(value) >= 0;
          },
        )
        .test(
          'max-value',
          `Export limit must be less than or equal to ${maxExportPower} MW.`,
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return Number(value) <= maxExportPower;
          },
        ),
      energyCapacity: string()
        .nullable()
        .transform((value) =>
          value === null || value === undefined ? '' : value,
        )
        .typeError('Please enter an energy limit for the reduction.')
        .required('Please enter an energy limit for the reduction.')
        .test(
          'is-valid-number',
          'Energy limit must be a valid number.',
          (value) => {
            if (value === null || value === undefined || value === '')
              return true; // Let required handle empty
            return !isNaN(Number(value));
          },
        )
        .test(
          'min-value',
          'Energy limit must be greater than or equal to zero.',
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return Number(value) >= 0;
          },
        )
        .test(
          'max-value',
          `Energy limit must be less than or equal to ${maxEnergyCapacity} MWh.`,
          (value) => {
            if (value === null || value === undefined || value === '')
              return true;
            return Number(value) <= maxEnergyCapacity;
          },
        ),
      repeatEndInclusive: repeatEndInclusiveField(),
    })
    .test('at-least-one-filled', function (value) {
      const { maxImportPower, maxExportPower } = value;

      const hasImportPower =
        maxImportPower !== null &&
        maxImportPower !== undefined &&
        maxImportPower !== '';

      const hasExportPower =
        maxExportPower !== null &&
        maxExportPower !== undefined &&
        maxExportPower !== '';

      if (hasImportPower || hasExportPower) {
        return true;
      }
      return this.createError({
        path: 'limits',
        message: 'At least one of Export limit or Import limit must be filled.',
      });
    });
